import type { WorkspaceRiskCatalogItem } from "@unbox/api";
import { AlertTriangle, Check, CheckCircle } from "@untitledui/icons";
import type { ReactNode } from "react";
import type { MerchantDraftItem } from "@/api/workspace-merchants";
import { buildRiskGroupsFromCatalog } from "@/components/safety-config/risk-groups";
import { SUPPORTED_RISK_EXPLANATION_LANGUAGES } from "@/utils/risk-explanation-languages";
import type { HandlingForm } from "./step-configure-risks";
import type { CredentialGroup } from "./step-connect-tiktok";
import type { DetailsValue } from "./step-details";
import type { DetectionValue } from "./step-detect-risks";

const REGION_LABELS: Record<CredentialGroup, string> = { UK: "UK & EU", US: "US", SEA: "SEA", JP: "Japan" };

const Section = ({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) => (
  <section className="flex flex-col gap-4 rounded-xl border border-secondary bg-white p-6">
    <div className="flex items-center justify-between">
      <h2 className="text-md font-semibold text-primary">{title}</h2>
      <button type="button" onClick={onEdit} className="text-sm font-semibold text-brand-700 hover:text-brand-800">
        Edit
      </button>
    </div>
    {children}
  </section>
);

const Detail = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[200px_1fr] gap-4 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="font-semibold text-primary">{children}</span>
  </div>
);

const ConnectionStatus = ({ connected }: { connected: boolean }) =>
  connected ? (
    <span className="flex items-center gap-1.5 text-success-700">
      <CheckCircle className="size-4" />
      Connected
    </span>
  ) : (
    <span className="flex items-center gap-1.5 text-warning-700">
      <AlertTriangle className="size-4" />
      Awaiting approval in TikTok
    </span>
  );

export const StepReview = ({
  draft,
  details,
  detection,
  handling,
  credentialGroup,
  catalog,
  onEdit,
}: {
  draft: MerchantDraftItem;
  details: DetailsValue;
  detection: DetectionValue | null;
  handling: HandlingForm;
  credentialGroup: CredentialGroup | null;
  catalog: WorkspaceRiskCatalogItem[];
  onEdit: (step: number) => void;
}) => {
  const connected = !!draft.shopConnectedAt;
  const selectedIds = new Set(detection?.risks.map((risk) => risk.riskId) ?? []);
  const allGroups = buildRiskGroupsFromCatalog(catalog.filter((risk) => risk.category !== "custom"));
  const isSelected = (keys: string[]) => keys.some((key) => selectedIds.has(key));
  const coreGroup = allGroups.find((group) => group.id === "baseline");
  const extraGroups = allGroups.filter((group) => group.id !== "baseline");
  const extraTotal = extraGroups.reduce((sum, group) => sum + group.items.length, 0);
  const extraSelected = extraGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) => isSelected(item.keys ?? [item.key])),
  }));
  const extraCount = extraSelected.reduce((sum, group) => sum + group.items.length, 0);
  const autoApproved = handling.risks.filter((risk) => risk.autoConfirm && selectedIds.has(risk.riskId)).length;
  const language = SUPPORTED_RISK_EXPLANATION_LANGUAGES.find((l) => l.code === handling.language);

  return (
    <div className="flex flex-col gap-4">
      {!connected && (
        <div
          role="alert"
          className="flex gap-3 rounded-xl border border-warning-300 bg-warning-25 p-4 text-sm text-warning-700"
        >
          <AlertTriangle className="mt-0.5 size-5 shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="font-semibold">Your TikTok shop's not connected yet. Connect this to finish setup.</p>
            <p>
              Someone with permission needs to approve access in TikTok Shop before setup can be completed. Save now,
              then pick this up again with the share link from the Connect step.
            </p>
          </div>
        </div>
      )}

      <Section title="Merchant details" onEdit={() => onEdit(1)}>
        <Detail label="Handle">@{details.handle}</Detail>
        <Detail label="TikTok Shop region">
          {credentialGroup ? REGION_LABELS[credentialGroup] : "Not chosen yet"}
        </Detail>
        <Detail label="Merchant name">{details.name}</Detail>
        <Detail label="Creator message language">{language?.english ?? language?.name ?? handling.language}</Detail>
        <Detail label="Merchant logo">
          {draft.logoUrl ? (
            <img src={draft.logoUrl} alt="Merchant logo" className="h-10 max-w-[120px] rounded object-contain" />
          ) : (
            <span className="font-normal text-gray-400">Not uploaded</span>
          )}
        </Detail>
      </Section>

      <Section title="What we'll monitor" onEdit={() => onEdit(2)}>
        {coreGroup && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-primary">
              Core safety{" "}
              <span className="font-normal text-gray-500">· Always on · {coreGroup.items.length} risks</span>
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              {coreGroup.items.map((item) => (
                <li key={item.key} className="flex items-center gap-2 text-sm text-secondary">
                  <Check className="size-4 text-success-600" />
                  {item.name}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-primary">
            Additional risks selected{" "}
            <span className="font-normal text-gray-500">
              · {extraCount} of {extraTotal}
            </span>
          </p>
          {extraSelected
            .filter((group) => group.items.length > 0)
            .map((group) => (
              <div key={group.id} className="flex flex-col gap-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.name}</p>
                <ul className="flex flex-wrap gap-x-6 gap-y-1">
                  {group.items.map((item) => (
                    <li key={item.key} className="flex items-center gap-2 text-sm text-secondary">
                      <Check className="size-4 text-success-600" />
                      {item.name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      </Section>

      <Section title="Risk actions" onEdit={() => onEdit(3)}>
        <p className="text-sm text-secondary">
          {handling.mode === "skip"
            ? "Every risk is reviewed by hand until you configure actions."
            : `${autoApproved} ${autoApproved === 1 ? "risk is" : "risks are"} auto-approved when detected; the rest are sent for review.`}
        </p>
      </Section>

      <Section title="TikTok connections" onEdit={() => onEdit(4)}>
        <Detail label="TikTok Shop access">
          <ConnectionStatus connected={!!draft.shopConnectedAt} />
        </Detail>
      </Section>
    </div>
  );
};
